import React, { useState } from 'react';
import { ArrowUpRight, Compass, FileText, Network, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import useYamlData from '../hooks/useYamlData';
import SystemMark from '../components/SystemMark';
import { SURFACE_CYCLE, TILT_CYCLE } from '../components/PlanetSurface';
import '../styles/Career.css';

// Same tone -> icon pairing as the homepage discipline cards, so a colour and
// a glyph mean the same discipline everywhere on the site.
const DISCIPLINE_ICON = {
  delivery: Network,
  leadership: Compass,
  security: ShieldCheck,
  ai: Sparkles,
};

// resume.yaml carries the same light inline markup the Resume page allows.
const ALLOWED_TAGS = ['strong', 'i', 'em', 'u'];
const richText = (html) => ({
  __html: DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS, ALLOWED_ATTR: [] }),
});

// The one bolded phrase worth pulling out of a challenge's achievements --
// prefer one with a digit in it (an actual metric) over a qualitative phrase.
const extractMetric = (achievements = []) => {
  const strongs = achievements.flatMap((text) =>
    [...text.matchAll(/<strong>(.*?)<\/strong>/g)].map((match) => match[1]),
  );
  return strongs.find((phrase) => /\d/.test(phrase)) ?? strongs[0] ?? null;
};

/*
 * The interactive read of "impact across disciplines": one bar per resume
 * challenge, coloured by its tone and sized by how much it took to solve --
 * selecting a bar swaps in that challenge's own achievements below. This is a
 * different shape than the In Practice orbit on purpose: that page is about
 * which culture did the work, this one is about how much each push mattered.
 */
const ChallengeImpact = ({ challenges }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const maxCount = Math.max(...challenges.map((challenge) => challenge.achievements.length));
  const open = challenges[openIndex];

  return (
    <div className="challenge-impact">
      <div className="impact-bars" role="tablist" aria-label="Challenges, sized by impact">
        {challenges.map((challenge, index) => {
          const isOpen = index === openIndex;
          const pct = Math.max(30, Math.round((challenge.achievements.length / maxCount) * 100));
          return (
            <button
              type="button"
              key={challenge.name}
              className={`impact-bar${isOpen ? ' is-open' : ''}`}
              style={{ '--tone': `var(--tmc-signal-${challenge.tone ?? 'systems'})` }}
              role="tab"
              aria-selected={isOpen}
              onClick={() => setOpenIndex(index)}
            >
              <span className="impact-bar-label">{challenge.name}</span>
              <span className="impact-bar-track">
                <span className="impact-bar-fill" style={{ width: `${pct}%` }} />
              </span>
            </button>
          );
        })}
      </div>
      {open && (
        <div
          className="impact-detail"
          role="tabpanel"
          style={{ '--tone': `var(--tmc-signal-${open.tone ?? 'systems'})` }}
        >
          <p className="impact-detail-desc">{open.description}</p>
          <ul>
            {open.achievements.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={richText(item)} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/*
 * The employer's own mark, on a white chip in both themes -- the same treatment
 * the resume and the in-practice thread use, so one company looks like one company
 * wherever it appears on the site.
 */
const EmployerMark = ({ role }) =>
  role.logo ? (
    <span className="employer-mark">
      <img
        src={`${process.env.PUBLIC_URL}/logos/${role.logo}`}
        alt={`${role.organization.split(' \u00b7 ')[0]} logo`}
        loading="lazy"
      />
    </span>
  ) : null;

const Career = () => {
  const [view, setView] = useState('atlas');
  const { data, loading, error } = useYamlData('/data/portfolio.yaml');
  const { data: resume } = useYamlData('/data/resume.yaml');

  if (loading) return <p className="tmc-state">Loading career overview...</p>;
  if (error) return <p className="tmc-state tmc-state--error">{error}</p>;

  const roles = data.career;
  // resume.yaml is the deeper source: same employer, keyed the same way, with
  // per-challenge tone, description and achievements the atlas summary doesn't carry.
  const experienceByKey = new Map((resume?.experience ?? []).map((job) => [job.key, job]));

  return (
    <div className="career-page">
      <header className="career-intro page-shell">
        <p className="eyebrow">Career</p>
        <h1>A career built around making complex work move.</h1>
        <p>
          Explore the visual career atlas, or switch to a condensed timeline.
          The full, detailed resume lives on its own page.
        </p>
        <div className="career-controls">
          <div className="tmc-switch" role="group" aria-label="Career presentation">
            <button
              type="button"
              className={view === 'atlas' ? 'is-active' : ''}
              aria-pressed={view === 'atlas'}
              onClick={() => setView('atlas')}
            >
              <Sparkles size={16} aria-hidden="true" /> Atlas
            </button>
            <button
              type="button"
              className={view === 'timeline' ? 'is-active' : ''}
              aria-pressed={view === 'timeline'}
              onClick={() => setView('timeline')}
            >
              <FileText size={16} aria-hidden="true" /> Timeline
            </button>
          </div>
          <Link to="/resume" className="quiet-link">
            Full resume <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </header>

      {view === 'atlas' ? (
        <section className="career-atlas" aria-label="Career atlas">
          {roles.map((role, index) => {
            // Organization strings carry "Company · Industry" already, so the
            // industry chip costs no extra data.
            const industry = role.organization.split(' \u00b7 ')[1];
            const disciplines = role.disciplines ?? [];
            const job = experienceByKey.get(role.key);
            const challenges = job?.challenges ?? [];
            return (
              <article
                className="atlas-chapter"
                key={role.id}
                style={{ '--brand': `var(--tmc-employer-${role.key})` }}
              >
                <div className="atlas-index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="atlas-content">
                  <p className="eyebrow">{role.period}</p>
                  <h2>{role.theme}</h2>
                  <p className="atlas-role">{role.role}</p>
                  {/*
                    * The brand colour is a rule here, never the text. These tokens
                    * were solved for 4.5:1 against the card surface; on the lighter
                    * chapter bands three of the four fall to about 3.8:1, which is
                    * fine for a graphic and not for a word.
                    */}
                  <p className="atlas-organization">
                    <EmployerMark role={role} />
                    <span>{role.organization}</span>
                  </p>
                  {industry && <p className="atlas-industry">{industry}</p>}

                  {disciplines.length > 0 && (
                    <ul className="atlas-disciplines" aria-label="Disciplines this role exercised">
                      {disciplines.map((tone) => {
                        const discipline = data.disciplines.find((entry) => entry.tone === tone);
                        const Icon = DISCIPLINE_ICON[tone] ?? Sparkles;
                        return (
                          <li key={tone} className={`atlas-discipline atlas-discipline--${tone}`}>
                            <Icon size={13} aria-hidden="true" />
                            {discipline?.name ?? tone}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <p className="atlas-summary">{role.summary}</p>

                  {challenges.length > 0 ? (
                    <>
                      {/* The numbers, at a glance, before the how -- one per challenge. */}
                      <ul className="chapter-stats" aria-label="Impact at a glance">
                        {challenges.map((challenge) => {
                          const metric = extractMetric(challenge.achievements);
                          if (!metric) return null;
                          return (
                            <li
                              key={challenge.name}
                              style={{ '--tone': `var(--tmc-signal-${challenge.tone ?? 'systems'})` }}
                            >
                              <span
                                className="chapter-stat-value"
                                dangerouslySetInnerHTML={richText(metric)}
                              />
                              <span className="chapter-stat-label">{challenge.name}</span>
                            </li>
                          );
                        })}
                      </ul>
                      <ChallengeImpact challenges={challenges} />
                    </>
                  ) : (
                    <ul>
                      {role.outcomes.map((outcome) => (
                        <li key={outcome}>{outcome}</li>
                      ))}
                    </ul>
                  )}

                  {role.link && (
                    <Link to={role.link.to} className="quiet-link atlas-link">
                      {role.link.label} <ArrowUpRight size={16} aria-hidden="true" />
                    </Link>
                  )}
                </div>
                <div className="atlas-orbit">
                  <SystemMark
                    title={role.theme}
                    coreRadius={46}
                    surface={SURFACE_CYCLE[index % SURFACE_CYCLE.length]}
                    tilt={TILT_CYCLE[index % TILT_CYCLE.length]}
                    color={`var(--tmc-chapter-band-${(index % 3) + 1})`}
                    rings={
                      // One ring per discipline the role drew on, each in that
                      // discipline's own colour -- the orbit is a legend, not
                      // decoration, and it mirrors the tag list beside it.
                      disciplines.length > 0
                        ? disciplines.map((tone, ringIndex) => ({
                            tilt: -34 + ringIndex * 22,
                            rx: 58 + ringIndex * 22,
                            ry: 16 + ringIndex * 6,
                            color: `var(--tmc-signal-${tone})`,
                            satellites: [
                              {
                                angle: 30 + ringIndex * 70 + index * 40,
                                color: `var(--tmc-signal-${tone})`,
                                r: 4,
                              },
                            ],
                          }))
                        : [
                            {
                              tilt: -34 + index * 19,
                              rx: 84,
                              ry: 20 + index * 7,
                              color: 'var(--tmc-action-primary)',
                              satellites: [{ angle: 25 + index * 47, color: 'var(--tmc-action-primary)', r: 5 }],
                            },
                          ]
                    }
                  />
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="timeline-view page-shell" aria-label="Career timeline">
          <ol className="timeline-track">
            {roles.map((role) => (
              <li className="timeline-row" key={role.id}>
                <div
                  className="timeline-rail"
                  aria-hidden="true"
                  style={{ '--brand': `var(--tmc-employer-${role.key})` }}
                >
                  <span className={`timeline-dot${role.period.includes('present') ? ' is-now' : ''}`} />
                </div>
                <article
                  className="timeline-entry"
                  style={{ '--brand': `var(--tmc-employer-${role.key})` }}
                >
                  <div className="timeline-head">
                    <div className="timeline-identity">
                      <p className="eyebrow">{role.period}</p>
                      <h2>{role.role}</h2>
                    </div>
                    <EmployerMark role={role} />
                  </div>
                  <p className="timeline-organization">{role.organization}</p>
                  <p>{role.summary}</p>
                  <ul>
                    {role.outcomes.map((outcome) => (
                      <li key={outcome}>{outcome}</li>
                    ))}
                  </ul>
                </article>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
};

export default Career;

